import { ReactNode, useMemo, useState } from 'react';
import { getMDXComponent } from 'mdx-bundler/client';
import { useDialogState, Dialog, DialogBackdrop } from 'reakit/Dialog';
import dynamic from 'next/dynamic';
import cn from 'classnames';

import BaseLayout from '@/components/Layout/Base';
import Header from '@/components/docs/header';
import Sidebar from '@/components/docs/sidebar';
import Button from '@/components/Button';
import SEO from '@/components/SEO';
import { components } from '@/components/docs/MDX';
import CloseSVG from '@/svg/icons/close.svg';
import type { DocumentTypes } from '.contentlayer/types';

const DynamicTOC = dynamic(() => import(`../docs/TOC`), { ssr: false });
const DynamicFeedbackBanner = dynamic(() => import(`../docs/feedback-banner`));
const DynamicFeedbackForm = dynamic(() => import(`../docs/feedback-form`), {
  ssr: false,
});

export interface DocsLayoutProps {
  doc: Partial<DocumentTypes>;
  children?: ReactNode;
  code?: string;
  fullWidth?: boolean;
  disableFeedback?: boolean;
}

export default function DocsLayout({
  doc,
  code,
  children,
  fullWidth,
  disableFeedback,
}: DocsLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const dialog = useDialogState();

  const toggleNav = () => setMenuOpen(!menuOpen);

  const Component = useMemo(() => {
    if (!code) return null;

    return getMDXComponent(code);
  }, [code]) as React.ElementType;

  const {
    title,
    category,
    meta_title,
    meta_description,
    disableTOC,
    headings,
  } = doc;

  const hasTOC = headings && headings.length > 0 && !disableTOC;

  return (
    <>
      <SEO title={meta_title || title} subTitle={meta_description} />

      <BaseLayout>
        <Header menuOpen={menuOpen} toggleNav={toggleNav} />

        <div className="w-full max-w-7xl mx-auto relative">
          <div className="md:flex relative">
            <Sidebar menuOpen={menuOpen} category={category || ``} />

            <main className="min-w-0 w-full flex-auto md:static h-full md:overflow-visible">
              <div className="px-6 md:px-8 py-12">
                <article
                  className={cn(`prose prose-docs`, {
                    '!max-w-full': fullWidth,
                  })}
                >
                  <h1>{title}</h1>

                  {code ? <Component components={components} /> : children}
                </article>

                {!disableFeedback && <DynamicFeedbackBanner dialog={dialog} />}
              </div>
            </main>

            {!fullWidth && (
              <div className="hidden lg:block xl:text-sm flex-none w-64 pl-8 mr-8 h-full sticky top-12 py-12">
                {hasTOC && !fullWidth && <DynamicTOC headings={headings} />}
              </div>
            )}
          </div>

          <DialogBackdrop
            {...dialog}
            className="bg-modal h-screen w-screen fixed inset-0 overflow-auto z-modal flex items-center justify-center"
          >
            <Dialog
              {...dialog}
              aria-label="Feedback form"
              className="align-bottom bg-white rounded-lg mx-4 px-4 pt-5 pb-4 text-left shadow-xl transform transition-all my-8 sm:align-middle w-full md:max-w-lg"
            >
              <div>{dialog.visible && <DynamicFeedbackForm />}</div>
              <div className="block absolute top-0 right-0 pt-4 pr-4">
                <Button
                  id="close-button"
                  appearance="minimal"
                  colour="dark"
                  size="small"
                  onClick={dialog.toggle}
                  className="hover:text-cornflower"
                  aria-label="Close"
                >
                  <CloseSVG className="h-4" aria-hidden="true" />
                </Button>
              </div>
            </Dialog>
          </DialogBackdrop>
        </div>
      </BaseLayout>
    </>
  );
}
